import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm, moduleOn } from "@/lib/perms";
import { toast } from "sonner";
import { Sparkles, ArrowRight, X, CheckCircle2, PackageCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";

/* Pawkt tokens (shared with Command Center) */
const C = {
  surface: "#FFFFFF", elevated: "#F5F1EA", primary: "#7C5CFC", primaryDeep: "#5837D8",
  primarySoft: "#EEE9FE", primaryLight: "#B8A5FD", text: "#1F1B18", textSec: "#7B6D62",
  textDis: "#B5A89D", border: "#EEE8DD", soon: "#B8791F",
};

/* HIGH / MEDIUM — opportunity STRENGTH, never a probability. Text label + colour. */
const STRENGTH = {
  HIGH: { label: "High opportunity", bg: "#EEE9FE", fg: "#5837D8" },
  MEDIUM: { label: "Medium opportunity", bg: "#FBF3E8", fg: "#B8791F" },
};

export const businessPropensityOn = (user) =>
  moduleOn(user, "prediction_engine") && moduleOn(user, "business_propensity_prediction");

function Card({ p, canManage, onDismiss, onActed, busy, nav }) {
  const s = STRENGTH[p.confidence] || STRENGTH.MEDIUM;
  return (
    <article data-testid={`bop-card-${p.id}`} className="rounded-[18px] p-5 flex flex-col"
      style={{ background: C.surface, border: `1px solid ${C.border}`, borderLeft: `4px solid ${C.primary}` }}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-wide" style={{ color: C.primaryDeep }}>
          <Sparkles size={14} /> Predicted opportunity
        </span>
        <span data-testid={`bop-strength-${p.id}`} className="rounded-full px-2.5 py-0.5 text-[11px] font-bold"
          style={{ background: s.bg, color: s.fg }}>{s.label}</span>
      </div>
      <h3 className="font-bold text-sm" style={{ color: C.text }} data-testid={`bop-title-${p.id}`}>{p.title}</h3>
      {p.customer_name && <p className="mt-0.5 text-[13px]" style={{ color: C.textSec }}>Customer: <span className="font-semibold">{p.customer_name}</span>{p.pet_name ? <> · Pet: <span className="font-semibold">{p.pet_name}</span></> : null}</p>}

      <div className="mt-3 rounded-[12px] p-3" style={{ background: C.elevated }}>
        <p className="text-[11px] font-extrabold uppercase tracking-wide mb-1.5" style={{ color: C.textSec }}>Why Pawkt is showing this</p>
        <ul className="space-y-1" data-testid={`bop-evidence-${p.id}`}>
          {(p.evidence || []).map((e, i) => (
            <li key={i} className="flex items-start gap-1.5 text-[13px]" style={{ color: C.text }}>
              <span aria-hidden className="mt-1.5 inline-block w-1.5 h-1.5 rounded-full shrink-0" style={{ background: C.primaryLight }} /> {e}
            </li>
          ))}
        </ul>
      </div>

      {p.offer_name && (
        <div className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-semibold" style={{ color: C.primaryDeep }} data-testid={`bop-offer-${p.id}`}>
          <PackageCheck size={15} /> Available: {p.offer_name}
        </div>
      )}
      {typeof p.score === "number" && (
        <p className="mt-1 text-[12px]" style={{ color: C.textDis }} data-testid={`bop-score-${p.id}`}>
          Opportunity score: {p.score} / 100 · a ranking strength, not a purchase probability
        </p>
      )}

      <div className="mt-auto pt-4 flex flex-wrap gap-2">
        <button onClick={() => nav(p.customer_id ? `/customers/${p.customer_id}` : "/customers")}
          data-testid={`bop-view-${p.id}`} className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[13px] font-bold"
          style={{ background: C.primary, color: "#fff" }}>View customer <ArrowRight size={14} /></button>
        {canManage && (
          <>
            <button onClick={() => onActed(p.id)} disabled={busy} data-testid={`bop-acted-${p.id}`}
              className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[13px] font-bold disabled:opacity-50"
              style={{ background: C.surface, color: C.primaryDeep, border: `1px solid ${C.primaryLight}` }}>
              <CheckCircle2 size={14} /> Mark acted on
            </button>
            <button onClick={() => onDismiss(p.id)} disabled={busy} data-testid={`bop-dismiss-${p.id}`}
              className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[13px] font-bold disabled:opacity-50"
              style={{ background: C.surface, color: C.textSec, border: `1px solid ${C.border}` }}>
              <X size={14} /> Dismiss
            </button>
          </>
        )}
      </div>
    </article>
  );
}

/**
 * Read-only consumption of the business-propensity backend. scope: "command" | "customer" | "pet".
 * Renders nothing when either feature flag is OFF (no polling). No duplicate prediction logic.
 */
export default function BusinessOpportunities({ scope = "command", customerId, petId }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  const enabled = businessPropensityOn(user) && (scope !== "customer" || !!customerId) && (scope !== "pet" || !!petId);
  const canManage = hasPerm(user, "predictions.manage");

  const params = { propensity_status: "active", limit: scope === "command" ? 9 : 3 };
  if (scope === "customer") params.customer_id = customerId;
  if (scope === "pet") params.pet_id = petId;
  const key = ["business-propensity", scope, customerId || "", petId || ""];

  const { data, isLoading, isError, error } = useQuery({
    queryKey: key,
    enabled,
    queryFn: async () => (await api.get("/business-propensity", { params })).data,
  });

  const mutate = useMutation({
    mutationFn: ({ id, action }) => api.post(`/business-propensity/${id}/${action}`),
    onSuccess: (_r, v) => { qc.invalidateQueries({ queryKey: ["business-propensity"] }); toast.success(v.action === "dismiss" ? "Opportunity dismissed" : "Marked as acted on"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  if (!enabled) return null;

  const items = data?.items || [];
  const Shell = ({ children }) => (
    <section className="mb-10" data-testid="bop-section">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles size={15} style={{ color: C.primary }} />
        <h2 style={{ color: C.text, fontSize: "0.8125rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase" }}>
          {scope === "command" ? "Predicted Opportunities" : "Business Opportunities"}
        </h2>
      </div>
      {children}
    </section>
  );

  if (isLoading) {
    return <Shell><div data-testid="bop-loading" className="animate-pulse grid gap-3 sm:grid-cols-2">
      <div className="h-40 rounded-[18px]" style={{ background: C.elevated }} />
      <div className="h-40 rounded-[18px]" style={{ background: C.elevated }} />
    </div></Shell>;
  }
  if (isError) {
    // 403 (no reports.view) => stay quiet; surface genuine errors honestly, never fake success
    if (error?.response?.status === 403) return null;
    return <Shell><div data-testid="bop-error" className="rounded-[18px] p-5 text-sm"
      style={{ background: C.surface, border: `1px dashed #DFD6C6`, color: "#C8482A" }}>
      Couldn't load opportunities right now. {apiErr(error)}</div></Shell>;
  }
  if (!items.length) {
    return <Shell><div data-testid="bop-empty" className="rounded-[18px] p-6 flex items-center gap-3"
      style={{ background: C.surface, border: "1px dashed #DFD6C6" }}>
      <Sparkles size={18} style={{ color: C.textDis }} />
      <p className="text-sm" style={{ color: C.textSec }}>No active opportunities yet — they appear as repeat usage patterns build up.</p>
    </div></Shell>;
  }

  return (
    <Shell>
      <div className={`grid gap-3 ${scope === "command" ? "sm:grid-cols-2 lg:grid-cols-3" : "sm:grid-cols-1"}`} data-testid="bop-grid">
        {items.map((p) => (
          <Card key={p.id} p={p} canManage={canManage} busy={mutate.isPending} nav={nav}
            onDismiss={(id) => mutate.mutate({ id, action: "dismiss" })}
            onActed={(id) => mutate.mutate({ id, action: "acted-on" })} />
        ))}
      </div>
    </Shell>
  );
}
